import React, { useCallback, useContext, useLayoutEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'

import { AppContext } from 'app/AppContext'
import { AppUserContext } from 'app/AppUserContext'
import { useAuthContext } from 'app/AuthContext/AuthContext'
import { invalidateNavigation } from 'data/hooks/navigation'
import { invalidatePages } from 'data/hooks/pages'
import { createNewStackData, useCreateStack } from 'data/hooks/stacks'
import { invalidateViews } from 'data/hooks/views'
import { AppTemplateDetail } from 'features/templates/AppTemplateDetail'
import { AppTemplateList } from 'features/templates/AppTemplateList'
import {
    TemplateModalContentInnerStyle,
    TemplateModalContentStyle,
} from 'features/templates/TemplateModal.css'
import { defaultColor } from 'features/workspace/forms/colorConstants'
import { ColorPicker } from 'features/workspace/forms/ColorPickerForm'
import { getAfterCreateRedirect } from 'features/workspace/getAfterCreateRedirect'
import analytics from 'utils/analytics'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { Field } from 'ui/components/Field'
import { IconName } from 'ui/components/Icon/Icon'
import { IconPickerDropdown } from 'ui/components/IconPicker'
import { Input } from 'ui/components/Input'
import {
    Modal,
    ModalCloseTrigger,
    ModalContent,
    ModalContentInner,
    ModalFooter,
    ModalFooterButtonGroup,
    ModalHeader,
} from 'ui/components/Modal'
import { useToast } from 'ui/components/Toast'
import { useResponsiveValue } from 'ui/styling/helpers/useResponsiveValue'

import { useTemplatesPaneState } from './hooks/useTemplatesPaneState'
import { ModalPane } from './createAppModalTypes'

// BE truncates name above this length (stack name is used in pg identifiers so needs to be short)
const MAX_STACK_NAME_LENGTH = 25

type CreateAppFormData = {
    appName: string
    brandColor: string
    logo: string
    icon: string
    icon_family: string
}

type CreateAppModalProps = {
    isOpen: boolean
    onClose: () => void
    onBeforeCreateStack: (data: Partial<StackDto>) => void
    preselectedDataSourceType?: string
    spaceName?: string
    defaultPane?: ModalPane
}

export const CreateAppModal: React.FC<CreateAppModalProps> = ({
    isOpen,
    onClose,
    onBeforeCreateStack,
    preselectedDataSourceType,
    spaceName,
    defaultPane = ModalPane.CreateApp,
}) => {
    const [currentPane, setCurrentPane] = useState(defaultPane)

    const isTemplatesPane = currentPane === ModalPane.Templates

    // Reset the pane when the modal is closed
    useLayoutEffect(() => {
        if (isOpen) return

        return () => {
            setCurrentPane(defaultPane)
        }
    }, [defaultPane, isOpen])

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                onClose()
            }
        },
        [onClose]
    )

    return (
        <Modal open={isOpen} onOpenChange={onOpenChange}>
            <ModalContent
                zIndex={1300}
                className={isTemplatesPane ? TemplateModalContentStyle : undefined}
            >
                {currentPane === ModalPane.CreateApp && (
                    <CreateAppModalPane
                        onBeforeCreateStack={onBeforeCreateStack}
                        preselectedDataSourceType={preselectedDataSourceType}
                        spaceName={spaceName}
                        onClose={onClose}
                    />
                )}
                {isTemplatesPane && (
                    <TemplatesPane setCurrentPane={setCurrentPane} spaceName={spaceName} />
                )}
            </ModalContent>
        </Modal>
    )
}

type CreateAppModalPaneProps = Pick<
    CreateAppModalProps,
    'onBeforeCreateStack' | 'preselectedDataSourceType' | 'spaceName' | 'onClose'
> & {}

const CreateAppModalPane: React.FC<CreateAppModalPaneProps> = ({
    onBeforeCreateStack,
    preselectedDataSourceType,
    spaceName,
    onClose,
}) => {
    const history = useHistory()
    const toast = useToast()

    const { mutateAsync: createStack } = useCreateStack({
        onError: (err) => {
            console.error(err)
            toast({
                type: 'error',
                title: 'There was a problem creating the app',
                startIcon: { name: 'AlertCircle' },
                helperText: 'Please try again later. If the issue persists, contact support.',
            })
        },
    })

    const { workspaceAccount, workspaceZone } = useContext(AppContext)
    const { user } = useAuthContext()
    const { isPreviewingAsUserOrRole } = useContext(AppUserContext)

    const handleSave = async ({ appName, brandColor, logo, icon }: CreateAppFormData) => {
        if (!workspaceAccount?._sid || !workspaceZone?._sid || !user?._sid) {
            return
        }

        if (!brandColor) {
            brandColor = workspaceAccount.options?.theme?.brandColor || defaultColor
        }
        const data = createNewStackData(
            workspaceAccount._sid,
            workspaceZone._sid,
            user._sid,
            appName,
            icon,
            logo,
            brandColor
        ) as Partial<StackDto>

        onBeforeCreateStack?.(data)

        analytics.track('app created submitted', {
            workspace_id: workspaceAccount._sid,
            is_impersonating: isPreviewingAsUserOrRole,
            user_id: user?._sid,
            event_description: 'New app created submitted',
            event_category: 'app',
        })

        const stack = await createStack(data)
        history.push(getAfterCreateRedirect(stack, preselectedDataSourceType))

        // Invalidate nav related items when creating a new stack, as the new stack will come with some of these
        // for the Stacker Users table.
        invalidateViews()
        invalidatePages()
        invalidateNavigation()
        onClose()
    }

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        watch,
        setValue,
    } = useForm<CreateAppFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            icon: 'book-02',
            brandColor: workspaceAccount?.options?.theme?.brandColor || defaultColor,
        },
    })

    const icon = watch('icon')
    const brandColor = watch('brandColor')

    return (
        <Box as="form" onSubmit={handleSubmit(handleSave)}>
            <ModalHeader title="Create a new app" subtitle={spaceName} />
            <ModalContentInner>
                <Box flex column gap="l">
                    <Input
                        required
                        label="Name"
                        maxLength={MAX_STACK_NAME_LENGTH}
                        {...register('appName', { required: true })}
                        autoFocus
                        isError={!!errors?.appName}
                        helperText={errors?.appName && 'Name is required'}
                    />
                    <Box flex center gap="m">
                        <Box noShrink>
                            <Field
                                label="Icon"
                                isError={!!errors?.icon}
                                helperText={errors?.icon && 'Icon is required'}
                            >
                                <IconPickerDropdown
                                    variant="hugeicons"
                                    value={
                                        icon
                                            ? {
                                                  name: icon as IconName<'hugeicons'>,
                                                  type: 'hugeicons',
                                              }
                                            : undefined
                                    }
                                    onChange={(newValue) => {
                                        setValue('icon', newValue?.name ?? '')
                                        setValue('icon_family', newValue?.type ?? '')
                                    }}
                                />
                            </Field>
                        </Box>
                        <Box noShrink>
                            <Field
                                label="Color"
                                isError={!!errors?.brandColor}
                                helperText={errors?.brandColor && 'App color is required'}
                            >
                                <ColorPicker
                                    value={brandColor}
                                    onChange={(value) => setValue('brandColor', value)}
                                />
                            </Field>
                        </Box>
                    </Box>
                </Box>
            </ModalContentInner>
            <ModalFooter>
                <ModalFooterButtonGroup>
                    <Button size="l" type="submit" disabled={!isValid}>
                        Create
                    </Button>
                </ModalFooterButtonGroup>
            </ModalFooter>
        </Box>
    )
}

type TemplatesPaneProps = {
    setCurrentPane: (pane: ModalPane) => void
    spaceName?: string
}

const TemplatesPane: React.FC<TemplatesPaneProps> = ({ setCurrentPane, spaceName }) => {
    const {
        templates,
        isDetail,
        selectedTemplate,
        isFetchingSlow,
        onBackToList,
        onStartFromScratch,
        onSelectTemplate,
        title,
        isError,
        onUseTemplate,
    } = useTemplatesPaneState({ setCurrentPane, spaceName })

    const backButtonText = useResponsiveValue({
        mobile: '',
        tablet: 'Back',
    })

    return (
        <>
            <ModalHeader
                title={title}
                showBackButton={isDetail}
                backButtonText={backButtonText}
                onBackButtonClick={onBackToList}
            />
            <ModalContentInner className={TemplateModalContentInnerStyle}>
                {isDetail && <AppTemplateDetail template={selectedTemplate!} />}
                {!isDetail && (
                    <AppTemplateList
                        templates={templates}
                        onSelectTemplate={onSelectTemplate}
                        isLoading={isFetchingSlow}
                        isError={isError}
                    />
                )}
            </ModalContentInner>
            <ModalFooter borderColor="border" borderTopWidth={1}>
                <ModalFooterButtonGroup layout="inline">
                    {isDetail && (
                        <Button size="l" onClick={onUseTemplate}>
                            Use this template
                        </Button>
                    )}
                    {!isDetail && (
                        <>
                            <ModalCloseTrigger asChild>
                                <Button size="l" variant="ghost">
                                    Cancel
                                </Button>
                            </ModalCloseTrigger>
                            <Button size="l" variant="secondary" onClick={onStartFromScratch}>
                                Start from scratch
                            </Button>
                        </>
                    )}
                </ModalFooterButtonGroup>
            </ModalFooter>
        </>
    )
}
